import { getFontCss, styleConstants } from '@paywithextend/components'
import { useQuery } from '@tanstack/react-query'
import React from 'react'
import { useDispatch, useSelector } from 'react-redux'
import useAnalyticsContext from 'src/analytics/useAnalyticsContext'
import { selectErrorDetailMapFromState } from 'src/app/RegisterAccount/redux/registerAccount.selectors'
import { createSetIsToastOpen } from 'src/app/Toast/redux/toast.actions'
import { ThankYouForSettingUpYourOrgToastMessage } from 'src/app/Toast/Toast'
import OrganizationFormClipped from 'src/assets/svgs/Organization-Form-Clipped.svg'
import Popup from 'src/components/Popup/Popup'
import RegisterOrganizationForm from 'src/components/RegisterOrganizationForm/RegisterOrganizationForm'
import Tooltip from 'src/components/Tooltip/Tooltip'
import { FormFieldsValidation, useFormFields } from 'src/hooks'
import { useTranslate } from 'src/localization/Trns'
import { closeModal } from 'src/navigation/Lnk'
import { useCreateOrganization } from 'src/queries/organizations/organizationCreate.queries'
import { industryOptionsFromConfig } from 'src/queries/session/config/config.options'
import { sessionConfigQueries } from 'src/queries/session/config/config.queries'
import { createAbandonPostOrganizationAction } from 'src/redux/actions/data/organizations.actions'
import { selectNullSafeLocaleFromState } from 'src/redux/selectors/auth.selectors'
import { selectMostRecentlyCreatedCreditCardListModelFromState } from 'src/redux/selectors/data/creditCards.selectors'
import {
  selectMostRecentlyCreatedCreditCardOrganizationListModelFromState,
  selectOrganizationTopLevelErrorFromState,
} from 'src/redux/selectors/data/organizations.selectors'
import { stringIsEmpty } from 'src/utils/validation/string.validation'
import { css } from 'styled-components'

export type RegisterOrganizationFields = {
  name?: string
  industry?: string
}

const RegisterOrganization: React.FC = () => {
  const translate = useTranslate()

  const errorDetailMap = useSelector(selectErrorDetailMapFromState)
  const locale = useSelector(selectNullSafeLocaleFromState)
  // Default [] preserves the legacy redux initial-state render (empty option
  // list while pending) — the config reducer had no loading/error flags.
  const { data: industryOptions = [] } = useQuery({
    ...sessionConfigQueries.byLocale(locale),
    select: industryOptionsFromConfig,
  })
  const { analytics } = useAnalyticsContext()
  const mostRecentCreditCard = useSelector(
    selectMostRecentlyCreatedCreditCardListModelFromState,
  )
  const mostRecentCreditCardOrganization = useSelector(
    selectMostRecentlyCreatedCreditCardOrganizationListModelFromState,
  )
  const organizationTopLevelError = useSelector(
    selectOrganizationTopLevelErrorFromState,
  )
  const dispatch = useDispatch()
  const { mutate: createOrganization } = useCreateOrganization()

  const registerOrganizationFieldsValidation =
    (): FormFieldsValidation<RegisterOrganizationFields> => ({
      name: [stringIsEmpty()],
      industry: [stringIsEmpty()],
    })
  const {
    fields: organizationFlatFields,
    fieldsErrors: organizationFlatFieldsErrors,
    onFieldChange: organizationFlatFieldChange,
    setErrorsAndReturnDidPassValidation: organizationFlatFieldsSetErrors,
  } = useFormFields<RegisterOrganizationFields>(
    {
      name: mostRecentCreditCard?.companyName,
      industry: mostRecentCreditCardOrganization?.industry,
    },
    registerOrganizationFieldsValidation,
    errorDetailMap,
    undefined,
  )

  const onRegisterOrganizationSubmit = (): void => {
    const organizationFlatFieldsPassedValidation =
      organizationFlatFieldsSetErrors()
    if (organizationFlatFieldsPassedValidation) {
      createOrganization({
        body: organizationFlatFields,
        customCloseModalOnSuccess: () => {
          dispatch(closeModal())
          dispatch(
            createSetIsToastOpen(true, {
              toastMessage: <ThankYouForSettingUpYourOrgToastMessage />,
            }),
          )
        },
      })
    }
  }

  const onRegisterOrganizationClose = (): void => {
    dispatch(createAbandonPostOrganizationAction())
    analytics?.track('setup org - abandon setup org - button press', {
      screen: '/setuporg',
      interaction: 'button press',
      intent: 'abandon setup org',
    })
  }

  return (
    <Popup
      onClose={onRegisterOrganizationClose}
      css={css`
        display: flex;
        flex-direction: column;
        position: relative;
      `}
    >
      <img
        src={OrganizationFormClipped}
        alt={''}
        css={css`
          position: absolute;
          top: 0;
          right: 0;
          border-top-right-radius: 10px;
        `}
      />
      <div
        css={css`
          display: flex;
          margin-bottom: 20px;
        `}
      >
        <span
          css={css`
            ${getFontCss(
              32,
              styleConstants.colors.black,
              styleConstants.fontFamilies.medium,
            )}
          `}
        >
          {translate('Set up your organization')}
        </span>
        <Tooltip openLeft={false}>
          {translate(
            'Setting up an organization opens up the ability to invite members and assign roles and permissions.',
          )}
        </Tooltip>
      </div>
      <RegisterOrganizationForm
        onFlatFieldChange={organizationFlatFieldChange}
        flatFields={organizationFlatFields}
        flatFieldsErrors={organizationFlatFieldsErrors}
        industryOptions={industryOptions}
        onSubmit={onRegisterOrganizationSubmit}
        topLevelError={organizationTopLevelError}
      />
    </Popup>
  )
}

export default RegisterOrganization
